import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import Slider from 'rc-slider';
import styles from './RangerSilder.module.css';
import cn from 'classnames'

const StyledSlider = styled(Slider)<{ theme: 'light' | 'dark' }>`
  .rc-slider-rail {
    position: absolute;
    left: 0;
    width: 100%;
    height: 2px;
    background-color: transparent;
    border-radius: 0;
  }

  .rc-slider-track {
    position: absolute;
    left: 0;
    height: 2px;
    background-color: transparent;
    border-radius: 0;
    transform: translateX(0);
  }

  .rc-slider-handle {
    position: absolute;
    width: 27px;
    height: 16px;
    background: ${({ theme }) => (theme === 'dark' ? 'var(--color-black)' : 'var(--color-white)')};
    border-radius: var(--border-radius-4);
    margin-top: calc(-1 * var(--spacing-2));
    cursor: grab;
    touch-action: pan-x;
    border: 2px solid ${({ theme }) => (theme === 'dark' ? 'var(--color-white)' : 'var(--color-black)')};
    transform: translateX(-50%);
    transition: background 0.2s;
  }

  .rc-slider-handle:hover {
    background: ${({ theme }) => (theme === 'dark' ? 'var(--color-dark-gray-button)' : 'var(--color-light-gray)')};
  }

  .rc-slider-handle:active {
    background-color: ${({ theme }) => (theme === 'dark' ? 'var(--color-white)' : 'var(--color-black)')};
  }

  .rc-slider-step {
    position: absolute;
    width: 100%;
    height: 2px;
    background: transparent;
  }
`;

interface RangeSliderProps {
  min: number;
  max: number;
  value: [number, number];
  onChange: (value: [number, number]) => void;
  label: string;
  step?: number;
  decimalPlaces?: number;
  formatValue?: (num: number) => string;
  parseValue?: (value: string) => number;
  theme: 'light' | 'dark';
}

const RangeSlider: React.FC<RangeSliderProps> = ({
  min,
  max,
  value,
  onChange,
  label,
  step = 1,
  decimalPlaces = 0,
  formatValue = (val) =>
    val.toLocaleString(undefined, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }),
  parseValue = (val) => parseFloat(val.replace(/ /g, '')),
  theme = 'light',
}) => {
  const [inputValues, setInputValues] = useState<[string, string]>([
    formatValue(value[0]),
    formatValue(value[1]),
  ]);
  const [isDefaultValue, setIsDefaultValue] = useState(true);

  const prevInputValuesRef = useRef<[string, string]>(inputValues);

  useEffect(() => {
    const formattedValues: [string, string] = [formatValue(value[0]), formatValue(value[1])];

    if (
      formattedValues[0] !== prevInputValuesRef.current[0] ||
      formattedValues[1] !== prevInputValuesRef.current[1]
    ) {
      setInputValues(formattedValues);
    }

    const isDefault = value[0] === min && value[1] === max;
    setIsDefaultValue(isDefault);

    prevInputValuesRef.current = formattedValues;
  }, [value, formatValue, min, max]);

  const handleInputChange = (index: 0 | 1, newValue: string) => {
    const sanitizedValue = newValue.replace(/[^0-9 ]/g, '');

    setInputValues((prev) => {
      const updatedValues = [...prev] as [string, string];
      updatedValues[index] = sanitizedValue;
      return updatedValues;
    });
  };

  const handleInputBlur = (index: 0 | 1) => {
    const parsedValue = parseValue(inputValues[index]);
    if (isNaN(parsedValue)) {
      setInputValues([formatValue(value[0]), formatValue(value[1])]);
      return;
    }

    const roundedValue = Math.round(parsedValue / step) * step;

    if (index === 0) {
      const adjustedValue = Math.min(roundedValue, value[1]);
      onChange([Math.max(adjustedValue, min), value[1]]);
    } else {
      const adjustedValue = Math.max(roundedValue, value[0]);
      onChange([value[0], Math.min(adjustedValue, max)]);
    }
  };

  const handleSliderChange = (sliderValue: number | number[]) => {
    if (Array.isArray(sliderValue)) {
      const [start, end] = sliderValue;
      const roundedStart = Math.round(start / step) * step;
      const roundedEnd = Math.round(end / step) * step;
      onChange([roundedStart, roundedEnd]);
    }
  };

  const handleInputFocus = (index: 0 | 1) => {
    if (inputValues[index] === formatValue(value[index])) {
      setInputValues((prev) => {
        const updatedValues = [...prev] as [string, string];
        updatedValues[index] = '';
        return updatedValues;
      });
    }
  };

  const wrapperClass = cn(styles.wrapper, {
    [styles.light]: theme === 'light',
    [styles.dark]: theme === 'dark',
  });

  return (
    <div className={wrapperClass}>
      <div className={styles.range_indicators}>
        <label className={cn(styles.range_label, 'text_subheaders_sh3')}>
          от{' '}
          <input
            type='text'
            value={inputValues[0]}
            onFocus={() => handleInputFocus(0)}
            onChange={(e) => handleInputChange(0, e.target.value)}
            onBlur={() => handleInputBlur(0)}
            className={cn('text_subheaders_sh3', isDefaultValue ? styles.input_inactive : styles.input_active)}
          />
        </label>
        <label className={cn(styles.range_label, 'text_subheaders_sh3')}>
          до{' '}
          <input
            type='text'
            value={inputValues[1]}
            onFocus={() => handleInputFocus(1)}
            onChange={(e) => handleInputChange(1, e.target.value)}
            onBlur={() => handleInputBlur(1)}
            className={cn('text_subheaders_sh3', isDefaultValue ? styles.input_inactive : styles.input_active)}
          />
          <span className={styles.range_label_span}>{label}</span>
        </label>
      </div>
      <div className={styles.range_slider}>
        <StyledSlider
          range
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleSliderChange}
          theme={theme}
        />
      </div>
    </div>
  );
};

export default RangeSlider;